import { Link } from "wouter";
import { ArrowRight, Check, MapPin, Clock, Truck, Shield, Zap, Target } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead, { createLocalBusinessSchema } from "@/components/SEOHead";
import AnimatedSection from "@/components/AnimatedSection";
import PageHero from "@/components/PageHero";
import { IMAGES, CONTACT } from "@/lib/constants";
import LazyImage from "@/components/LazyImage";

const localBenefits = [
  {
    icon: Truck,
    title: "Snabb leverans till Strängnäs",
    text: "Vi planerar leveransen från Eskilstuna till Strängnäs utifrån detaljernas omfattning, önskat datum och vald transportlösning.",
  },
  {
    icon: Clock,
    title: "Korta ledtider",
    text: "Tack vare tre CO2-lasrar och hög kapacitet kan vi hantera brådskande beställningar från Strängnäsregionen med kort varsel.",
  },
  {
    icon: Shield,
    title: "Garanterad precision",
    text: "Toleranser ner till 0,1 mm med moderna Trumpf- och Amada CO2-lasrar. Första bit-kontroll vid varje projektstart.",
  },
  {
    icon: Target,
    title: "Helhetslösning",
    text: "Kombinera laserskärning med kantpressning och svetsning – allt under ett tak. Färdiga komponenter levererade till Strängnäs.",
  },
];

const strangnasIndustries = [
  {
    name: "Försvarsindustri",
    desc: "Vi är en pålitlig partner till försvarsrelaterade företag i och runt Strängnäs, med erfarenhet av specialstål och höga krav.",
  },
  {
    name: "Bygg & Anläggning",
    desc: "Skräddarsydda metalldetaljer för byggprojekt i en växande region – fasader, stommar och specialkonstruktioner.",
  },
  {
    name: "Lantbruk & Jordbruk",
    desc: "Tillverkning och reparation av slitdelar och komponenter för jordbruksmaskiner i Sörmlands hjärta.",
  },
  {
    name: "Tillverkningsindustri",
    desc: "Prototyper och serietillverkning för lokala industriföretag i Strängnäs med omnejd. Flexibilitet och snabbhet är våra ledord.",
  },
];

const capabilities = [
  { material: "Svart stål", maxThickness: "25 mm", minThickness: "0,5 mm" },
  { material: "Rostfritt stål", maxThickness: "20 mm", minThickness: "0,5 mm" },
  { material: "Aluminium", maxThickness: "15 mm", minThickness: "0,5 mm" },
  { material: "Höghållfasthetsstål (Strenx)", maxThickness: "20 mm", minThickness: "1 mm" },
  { material: "Slitstål (Hardox/Raex)", maxThickness: "20 mm", minThickness: "1 mm" },
];

export default function LaserskarningStrangnas() {
  const localServiceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Laserskärning Strängnäs",
    description:
      "Professionell laserskärning för företag i Strängnäs. ASPAB erbjuder CO2 Laser-skärning med toleranser ner till 0,1 mm, snabba leveranser och brett materialutbud.",
    provider: {
      "@type": "LocalBusiness",
      name: "ASPAB",
      telephone: "+46161326 00",
      email: "order@aspab.com",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Abramsons väg 2",
        addressLocality: "Eskilstuna",
        postalCode: "632 39",
        addressCountry: "SE",
      },
    },
    areaServed: {
      "@type": "City",
      name: "Strängnäs",
      containedInPlace: {
        "@type": "Country",
        name: "Sweden",
      },
    },
    serviceType: "Laserskärning",
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: "Laserskärningstjänster",
      itemListElement: [
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning stål",
            description: "Laserskärning i svart stål upp till 25 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning rostfritt",
            description: "Laserskärning i rostfritt stål upp till 20 mm tjocklek",
          },
        },
        {
          "@type": "Offer",
          itemOffered: {
            "@type": "Service",
            name: "CO2 Laser-skärning aluminium",
            description: "Laserskärning i aluminium upp till 15 mm tjocklek",
          },
        },
      ],
    },
  };

  const localBusinessSchema = createLocalBusinessSchema({
    areaServed: "Strängnäs",
    serviceDescription: "Professionell laserskärning, kantpressning och svetsning för företag i Strängnäs och Mälardalen. Leveransplan bekräftas i offerten.",
  });

  return (
    <Layout>
      <SEOHead
        title="Laserskärning Strängnäs – Lokal leverantör | ASPAB"
        description="Laserskärning i Strängnäs med omnejd från ASPAB i Eskilstuna. Toleranser ner till 0,1 mm i stål, rostfritt och aluminium. Begär offert."
        canonical="https://aspab.com/laserskarning-strangnas"
        path="/laserskarning-strangnas"
        keywords="laserskärning Strängnäs, laserskärning Sörmland, plåtbearbetning Strängnäs, CO2 Laser Strängnäs, metallbearbetning Strängnäs"
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Strängnäs", href: "/laserskarning-strangnas" },
        ]}
        structuredData={[localServiceSchema, localBusinessSchema]}
        ogImage={IMAGES.ogHem}
      />

      <PageHero
        title="Laserskärning Strängnäs"
        subtitle="Din regionala partner för laserskärning i Strängnäs med precision, kvalitet och planerad leverans från ASPAB i Eskilstuna."
        breadcrumbs={[
          { name: "Laserskärning", href: "/tjanster/laserskarning" },
          { name: "Laserskärning Strängnäs", href: "/laserskarning-strangnas" },
        ]}
        image={IMAGES.heroLaser}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Laserskärning för Strängnäsregionen
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Partner för laserskärning i Strängnäs
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Söker ditt företag i Strängnäs en pålitlig leverantör av laserskurna detaljer? ASPAB i Eskilstuna erbjuder precis laserskärning med planerad leverans till hela regionen.
                </p>
                <p className="text-gray-500 mt-4 leading-relaxed">
                  Vi är en etablerad leverantör till industrin i Mälardalen, inklusive försvars-, bygg- och lantbrukssektorn i Strängnäs. Oavsett om du behöver prototyper eller stora serier levererar vi med precision.
                </p>
                <div className="flex items-center gap-3 mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
                  <MapPin className="w-5 h-5 text-[#2563EB] shrink-0" />
                  <div>
                    <span className="text-gray-900 font-medium text-sm">Leverans från ASPAB i Eskilstuna till Strängnäs</span>
                    <span className="text-gray-500 text-sm block">Via E20 – snabb och smidig leverans</span>
                  </div>
                </div>
                <Link
                  href="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-6"
                >
                  Begär offert <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <div className="space-y-4">
                <LazyImage
                  src={IMAGES.laserOldSite}
                  alt="Laserskärning för företag i Strängnäs – ASPAB CO2 Laser"
                  className="w-full rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4 mt-4">
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">0,1 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Tolerans</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">&lt;24h</div>
                    <div className="text-gray-500 text-sm mt-1">Leverans enkla detaljer</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">25 mm</div>
                    <div className="text-gray-500 text-sm mt-1">Max tjocklek stål</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-5 text-center border border-gray-100">
                    <div className="font-heading text-2xl font-bold text-[#2563EB]">30+</div>
                    <div className="text-gray-500 text-sm mt-1">Års erfarenhet</div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container">
          <AnimatedSection>
            <div className="max-w-2xl mb-14">
              <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                Fördelar
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                Varför företag i Strängnäs väljer ASPAB
              </h2>
              <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                Närhet, kapacitet och specialistkunskap. Vi kombinerar fördelarna med en lokal verkstad med musklerna hos en specialiserad laserskärningspartner.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {localBenefits.map((item, i) => (
              <AnimatedSection key={i} delay={i * 100}>
                <div className="bg-white p-6 rounded-lg border border-gray-200 h-full">
                  <item.icon className="w-8 h-8 text-[#2563EB]" />
                  <h3 className="font-heading text-lg font-bold text-gray-900 mt-4">{item.title}</h3>
                  <p className="text-gray-500 mt-2 text-sm">{item.text}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28 border-t">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-14 items-center">
            <AnimatedSection>
              <div>
                <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
                  Industrier i Strängnäs
                </span>
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-3">
                  Anpassad för Strängnäs lokala industri
                </h2>
                <p className="text-gray-500 mt-4 text-lg leading-relaxed">
                  Vi har erfarenhet av att arbeta med de nyckelindustrier som driver tillväxten i Strängnäs med omnejd. Från försvarsindustri till bygg och lantbruk.
                </p>
                <div className="mt-8 space-y-6">
                  {strangnasIndustries.map((industry, i) => (
                    <div key={i} className="flex gap-4">
                      <div className="w-10 h-10 bg-[#2563EB] text-white rounded-full flex items-center justify-center shrink-0 mt-1">
                        <Check className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-heading text-lg font-bold text-gray-900">{industry.name}</h4>
                        <p className="text-gray-500 text-sm mt-1">{industry.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
            <AnimatedSection delay={200}>
              <LazyImage
                src={IMAGES.workshop2}
                alt="Industrier i Strängnäs som använder laserskärning"
                className="w-full rounded-lg"
              />
            </AnimatedSection>
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-20 md:py-28 border-t border-gray-200">
        <div className="container text-center">
          <AnimatedSection>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900">
              Redo att starta ditt projekt i Strängnäs?
            </h2>
            <p className="text-gray-500 mt-4 text-lg max-w-2xl mx-auto">
              Kontakta oss idag för en kostnadsfri offert. Ladda upp dina ritningar (DXF/DWG) så återkommer vi snabbt med pris och förväntad leveranstid.
            </p>
            <Link
              href="/kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#2563EB] text-white font-semibold rounded-md hover:bg-[#1D4ED8] transition-colors mt-8 text-lg"
            >
              Begär offert nu <ArrowRight className="w-5 h-5" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </Layout>
  );
}
